import { useEffect, useState } from 'react';

interface TrustedByCarouselProps {
  excludeIheem?: boolean;
}

interface TrustedLogo {
  key: string;
  src: string;
  alt: string;
  height: number;
}

const TRUSTED_BY_LOGOS_JSON_URL =
  'https://innexpublic.blob.core.windows.net/platform-assets/organisations/trusted-by-logos.json';

function isTrustedLogo(value: unknown): value is TrustedLogo {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const logo = value as Partial<TrustedLogo>;
  return (
    typeof logo.key === 'string' &&
    typeof logo.src === 'string' &&
    typeof logo.alt === 'string' &&
    typeof logo.height === 'number'
  );
}

/**
 * TrustedByCarousel Component
 *
 * Displays an animated carousel of NHS trust logos.
 * Used on login and registration pages to show social proof.
 */
export default function TrustedByCarousel({ excludeIheem = false }: TrustedByCarouselProps) {
  const [logos, setLogos] = useState<TrustedLogo[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    fetch(TRUSTED_BY_LOGOS_JSON_URL, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch trusted logos (${response.status})`);
        }
        const payload = (await response.json()) as unknown;
        if (!Array.isArray(payload)) {
          throw new Error('Trusted logos payload must be an array');
        }
        const parsed = payload.filter(isTrustedLogo);
        if (parsed.length > 0) {
          setLogos(parsed);
        }
      })
      .catch(() => {
        // Keep defaults if remote config is unavailable.
      });

    return () => controller.abort();
  }, []);

  const filteredLogos = excludeIheem ? logos.filter((logo) => logo.key !== 'iheem') : logos;

  return (
    <div className="w-full flex flex-col items-center">
      <span className="font-raleway font-normal mb-4 text-content-subtle text-sm">Trusted by</span>
      <div
        className="relative w-full h-[70px] overflow-hidden"
        style={{ maskImage: 'linear-gradient(to right, transparent 0, black 10%, black 90%, transparent 100%)' }}
      >
        <div className="flex items-start animate-carousel gap-12" style={{ width: 'max-content' }}>
          {/* Repeat the logos twice for seamless looping */}
          {[...Array(2)].flatMap((_, i) =>
            filteredLogos.map((logo) => (
              <img
                key={`${logo.key}${i}`}
                src={logo.src}
                alt={logo.alt}
                className="object-contain filter grayscale"
                style={{ height: `${logo.height}px` }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
